import { BuyerProject } from "@/types/buyerProject";
import { ProcessDetails } from "@/types/processDetails";
import { PropertyType } from "@/types/property";
import { create } from "zustand";
import { devtools } from "zustand/middleware";
import { immer } from "zustand/middleware/immer";

// Helper function to build ID-to-boolean maps
const buildIdMap = <T>(items: T[], idSelector: (item: T) => string | undefined): Record<string, boolean> => {
	return items.reduce(
		(acc, item) => {
			const id = idSelector(item);
			if (id) acc[id] = true;
			return acc;
		},
		{} as Record<string, boolean>,
	);
};

// Basic types - keeping it simple for now
export interface BuyerUnit {
	id: string;
	unit?: {
		id: string;
	};
	processDetails?: ProcessDetails[];
	cancellation?: {
		flag: boolean;
		reason?: string;
	};
}

export interface BuyerPropertiesState {
	buyerUnits: BuyerUnit[];
	buyerProjects?: BuyerProject[];

	// Pre-computed shortlisted units for faster access
	shortlistedUnits: BuyerUnit[];
	shortlistedProjects?: BuyerProject[];

	// Pre-computed shortlisted IDs for O(1) access
	shortlistedUnitIds: Record<string, boolean>;
	shortlistedProjectIds: Record<string, boolean>;

	// Pre-computed visit requested units for faster access
	// TODO: Temporarily commented out - will be re-enabled later
	// visitRequestedUnits: BuyerUnit[];
	// visitRequestedProjects?: BuyerProject[];

	// Metadata
	lastFetchedAt: string | null;
	buyerGroupId: string | null;

	// Basic actions
	setBuyerUnits: (units: BuyerUnit[], buyerGroupId: string) => void;
	setBuyerProjects: (projects: BuyerProject[], buyerGroupId: string) => void;
	setShortlistedProjects: (projects: BuyerProject[]) => void;
	clearData: () => void;

	// Simple utility getters
	isPropertyShortlisted: (propertyId: string) => boolean;
	isProjectShortlisted: (projectId: string) => boolean;
	getShortlistedPropertyIds: () => string[];

	// Process stage checkers
	isPropertyInDecisionStage: (propertyId: string) => boolean;

	// Visit request utility getters
	// TODO: Temporarily commented out - will be re-enabled later - providing stubs for now
	isPropertyVisitRequested: (propertyId: string) => boolean;
	isProjectVisitRequested: (projectId: string) => boolean;
	getVisitRequestedPropertyIds: () => string[];

	// Optimistic updates for immediate UI response (suggestion cards)
	optimisticAddToShortlist: (propertyId: string, type: PropertyType) => void;
	optimisticRemoveFromShortlist: (propertyId: string, type: PropertyType) => void;

	// Manual optimistic updates for different mutation contexts (property overlay, etc.)
	optimisticAddToShortlistManual: (propertyId: string, type: PropertyType) => void;
	optimisticRemoveFromShortlistManual: (propertyId: string, type: PropertyType) => void;

	// Visit request optimistic updates
	// TODO: Temporarily commented out - will be re-enabled later - providing stubs for now
	optimisticAddToVisitRequest: (propertyId: string) => void;
	optimisticRemoveFromVisitRequest: (propertyId: string) => void;
	optimisticAddToVisitRequestManual: (propertyId: string) => void;
	optimisticRemoveFromVisitRequestManual: (propertyId: string) => void;
}

export const useBuyerPropertiesStore = create<BuyerPropertiesState>()(
	devtools(
		immer((set, get) => ({
			// Initial state
			buyerUnits: [],
			buyerProjects: [],
			shortlistedUnits: [],
			shortlistedProjects: [],
			shortlistedUnitIds: {},
			shortlistedProjectIds: {},
			// TODO: Temporarily commented out - will be re-enabled later
			// visitRequestedUnits: [],
			// visitRequestedProjects: [],
			lastFetchedAt: null,
			buyerGroupId: null,

			// Actions
			setBuyerUnits: (units: BuyerUnit[], buyerGroupId: string) => {
				set(state => {
					state.buyerUnits = units;
					state.buyerGroupId = buyerGroupId;
					state.lastFetchedAt = new Date().toISOString();

					// Pre-compute shortlisted units for faster access
					state.shortlistedUnits = units.filter(unit =>
						unit.processDetails?.some(pd => pd.process === "shortlisting" && pd.stage === "done"),
					);

					state.shortlistedUnitIds = buildIdMap(state.shortlistedUnits, (unit: BuyerUnit) => unit.unit?.id);

					// Pre-compute visit requested units for faster access
					// TODO: Temporarily commented out - will be re-enabled later
					// state.visitRequestedUnits = units.filter(unit =>
					// 	unit.processDetails?.some(pd => pd.process === "visiting" && pd.stage === "requested"),
					// );

					// Debug logging
					// console.log("Store Debug - setBuyerUnits:", {
					// 	totalUnits: units.length,
					// 	shortlistedUnits: state.shortlistedUnits.length,
					// 	// TODO: Temporarily commented out - will be re-enabled later
					// 	// visitRequestedUnits: state.visitRequestedUnits.length,
					// 	buyerGroupId,
					// 	shortlistedUnitIds: state.shortlistedUnits.map(u => u.unit?.id),
					// 	// visitRequestedUnitIds: state.visitRequestedUnits.map(u => u.unit?.id),
					// 	allUnitsWithProcesses: units.map(u => ({
					// 		id: u.unit?.id,
					// 		processDetails: u.processDetails,
					// 	})),
					// });
				});
			},

			setBuyerProjects: (projects: BuyerProject[], buyerGroupId: string) => {
				set(state => {
					state.buyerProjects = projects;
					state.buyerGroupId = buyerGroupId;
					state.lastFetchedAt = new Date().toISOString();

					// NOTE: We no longer compute shortlistedProjects here because we have a dedicated
					// getShortlistedProjects query that provides accurate data from the server.
					// The shortlistedProjects state is now managed by setShortlistedProjects() only.
				});
			},

			setShortlistedProjects: (projects: BuyerProject[]) => {
				set(state => {
					// Merge with existing optimistic updates instead of replacing
					// Keep temporary projects that were optimistically added
					const existingOptimisticProjects = (state.shortlistedProjects || []).filter(p =>
						p.id.startsWith("temp-project-"),
					);

					// Only keep optimistic projects that aren't already in the server data
					const serverProjectIds = new Set(projects.map(p => p.project?.id));
					const validOptimisticProjects = existingOptimisticProjects.filter(p => !serverProjectIds.has(p.project?.id));

					// Merge server data with valid optimistic updates
					state.shortlistedProjects = [...projects, ...validOptimisticProjects];

					state.shortlistedProjectIds = buildIdMap(
						state.shortlistedProjects ?? [],
						(project: BuyerProject) => project.project?.id,
					);
				});
			},

			clearData: () => {
				set(state => {
					state.buyerUnits = [];
					state.buyerProjects = [];
					state.shortlistedUnits = [];
					state.shortlistedProjects = [];
					state.shortlistedUnitIds = {};
					state.shortlistedProjectIds = {};
					// TODO: Temporarily commented out - will be re-enabled later
					// state.visitRequestedUnits = [];
					// state.visitRequestedProjects = [];
					state.lastFetchedAt = null;
					state.buyerGroupId = null;
				});
			},

			// Simple utility getters
			isPropertyShortlisted: (propertyId: string) => {
				const state = get();

				// Check optimized maps first
				if (state.shortlistedUnitIds[propertyId]) return true;
				if (state.shortlistedProjectIds && state.shortlistedProjectIds[propertyId]) return true;

				return false;
			},

			isProjectShortlisted: (projectId: string) => {
				const state = get();
				return !!state.shortlistedProjectIds[projectId];
			},

			getShortlistedPropertyIds: () => {
				const state = get();
				const unitIds = Object.keys(state.shortlistedUnitIds);
				const projectIds = state.shortlistedProjectIds ? Object.keys(state.shortlistedProjectIds) : [];

				return [...unitIds, ...projectIds];
			},

			// Process stage checkers
			isPropertyInDecisionStage: (propertyId: string) => {
				const state = get();

				// Check in units
				const unitInDecision = state.buyerUnits.some(unit => {
					const isMatchingUnit = unit.unit?.id === propertyId;
					const hasDecisionStage = unit.processDetails?.some(pd => pd.process === "decision" && pd.stage === "done");
					return isMatchingUnit && hasDecisionStage;
				});

				if (unitInDecision) {
					return true;
				}

				// Check in projects
				if (state.buyerProjects) {
					const projectInDecision = state.buyerProjects.some(project => {
						const isMatchingProject = project.project?.id === propertyId;
						const hasDecisionStage = project.processDetails?.some(
							pd => pd.process === "decision" && pd.stage === "done",
						);
						return isMatchingProject && hasDecisionStage;
					});
					return projectInDecision;
				}
				return false;
			},

			// Visit request utility getters
			// TODO: Temporarily providing stub implementations - will be re-enabled later
			isPropertyVisitRequested: (_propertyId: string) => {
				// Stub implementation - always returns false
				return false;
			},

			isProjectVisitRequested: (_projectId: string) => {
				// Stub implementation - always returns false
				return false;
			},

			getVisitRequestedPropertyIds: () => {
				// Stub implementation - always returns empty array
				return [];
			},

			// Visit request optimistic updates - stub implementations
			optimisticAddToVisitRequest: (_propertyId: string) => {
				// Stub implementation - does nothing
			},

			optimisticRemoveFromVisitRequest: (_propertyId: string) => {
				// Stub implementation - does nothing
			},

			optimisticAddToVisitRequestManual: (_propertyId: string) => {
				// Stub implementation - does nothing
			},

			optimisticRemoveFromVisitRequestManual: (_propertyId: string) => {
				// Stub implementation - does nothing
			},

			// TODO: Original implementation commented out below - will be re-enabled later
			/*
				COMMENTED OUT VISIT REQUEST METHODS - WILL BE RE-ENABLED LATER

				isPropertyVisitRequested: (propertyId: string) => {
					const state = get();

					if (state.buyerUnits.length === 0) {

						return false;
					}

					const result = state.buyerUnits.some(unit => {
						// Check if this unit matches the property ID
						const isMatchingUnit = unit.unit?.id === propertyId;
						// Check if it has visiting process with requested stage
						const hasVisitRequested = unit.processDetails?.some(
							pd => pd.process === "visiting" && pd.stage === "requested",
						);
						const unitResult = isMatchingUnit && hasVisitRequested;

						if (isMatchingUnit) {
							console.log("Store Debug - isPropertyVisitRequested found match:", {
								propertyId,
								unitId: unit.unit?.id,
								hasVisitRequested,
								processDetails: unit.processDetails,
								unitResult,
							});
						}

						return unitResult;
					});

					console.log("Store Debug - isPropertyVisitRequested final:", {
						propertyId,
						result,
						totalUnits: state.buyerUnits.length,
					});
					return result;
				},

				isProjectVisitRequested: (projectId: string) => {
					const state = get();
					if (!state.visitRequestedProjects || state.visitRequestedProjects.length === 0) {
						console.log("Store Debug - isProjectVisitRequested: No visit requested projects", { projectId });
						return false;
					}

					// Log all project IDs in store for comparison
					const allProjectIds = state.visitRequestedProjects.map(p => p.project?.id);
					console.log("Store Debug - All visit requested project IDs in store:", allProjectIds);
					console.log("Store Debug - Looking for project ID:", projectId);

					// Use precomputed visit requested projects for faster access
					const result = state.visitRequestedProjects.some(project => {
						const match = project.project?.id === projectId;
						if (match) {
							console.log("Store Debug - isProjectVisitRequested found match:", {
								projectId,
								projectInStoreId: project.project?.id,
								processDetails: project.processDetails,
							});
						}
						return match;
					});

					console.log("Store Debug - isProjectVisitRequested final:", {
						projectId,
						result,
						totalProjects: state.visitRequestedProjects.length,
						projectIdType: typeof projectId,
						allStoredIds: allProjectIds,
					});
					return result;
				},

				getVisitRequestedPropertyIds: () => {
					const state = get();
					const unitIds = state.visitRequestedUnits.map(unit => unit.unit?.id).filter(Boolean) as string[];

					const projectIds = (state.visitRequestedProjects || [])
						.map(project => project.project?.id)
						.filter(Boolean) as string[];

					return [...unitIds, ...projectIds];
				},
				*/

			// Optimistic updates for immediate UI response
			optimisticAddToShortlist: (propertyId: string, type: PropertyType) => {
				set(state => {
					if (type === PropertyType.UNIT) {
						// Handle unit shortlisting
						let existingUnitIndex = state.buyerUnits.findIndex(unit => unit.unit?.id === propertyId);

						if (existingUnitIndex !== -1) {
							// Update existing unit
							const unit = state.buyerUnits[existingUnitIndex];
							if (!unit.processDetails) {
								unit.processDetails = [];
							}

							// Remove any existing shortlisting process
							unit.processDetails = unit.processDetails.filter(pd => pd.process !== "shortlisting");

							// Add new shortlisting process
							unit.processDetails.push({
								process: "shortlisting",
								status: "active",
								stage: "done",
								incomplete: false,
								appointmentDate: new Date(),
								visitExec: "",
								updatedAt: new Date(),
								updatedBy: "",
							});
						} else {
							// Create new unit entry
							const newUnit = {
								id: `temp-unit-${propertyId}-${Date.now()}`,
								unit: { id: propertyId },
								processDetails: [
									{
										process: "shortlisting",
										status: "active",
										stage: "done",
										incomplete: false,
										appointmentDate: new Date(),
										visitExec: "",
										updatedAt: new Date(),
										updatedBy: "",
									},
								],
							};
							state.buyerUnits.push(newUnit);
						}

						// Recompute shortlisted units
						state.shortlistedUnits = state.buyerUnits.filter(unit =>
							unit.processDetails?.some(pd => pd.process === "shortlisting" && pd.stage === "done"),
						);
						state.shortlistedUnitIds = buildIdMap(state.shortlistedUnits, (unit: BuyerUnit) => unit.unit?.id);
					} else if (type === PropertyType.PROJECT) {
						// Handle project shortlisting
						if (!state.buyerProjects) {
							state.buyerProjects = [];
						}

						let existingProjectIndex = state.buyerProjects.findIndex(project => project.project?.id === propertyId);

						if (existingProjectIndex !== -1) {
							// Update existing project
							const project = state.buyerProjects[existingProjectIndex];
							if (!project.processDetails) {
								project.processDetails = [];
							}

							// Remove any existing shortlisting process
							project.processDetails = project.processDetails.filter(pd => pd.process !== "shortlisting");

							// Add new shortlisting process
							project.processDetails.push({
								process: "shortlisting",
								status: "active",
								stage: "done",
								incomplete: false,
								appointmentDate: new Date(),
								visitExec: "",
								updatedAt: new Date(),
								updatedBy: "",
							});
						} else {
							// Create new project entry
							const processDetail = {
								process: "shortlisting",
								status: "active",
								stage: "done",
								incomplete: false,
								appointmentDate: new Date(),
								visitExec: "",
								updatedAt: new Date(),
								updatedBy: "",
							};
							const newProject = {
								id: `temp-project-${propertyId}-${Date.now()}`,
								project: { id: propertyId, name: "" },
								sellerProject: { id: propertyId },
								group: { id: state.buyerGroupId || "" },
								processDetails: [processDetail],
								currentProcessDetails: processDetail,
								cancellation: { flag: false },
							};
							state.buyerProjects.push(newProject);
						}

						// Recompute shortlisted projects
						state.shortlistedProjects = state.buyerProjects.filter(project =>
							project.processDetails?.some(pd => pd.process === "shortlisting" && pd.stage === "done"),
						);
						state.shortlistedProjectIds = buildIdMap(
							state.shortlistedProjects,
							(project: BuyerProject) => project.project?.id,
						);
					}
				});
			},

			optimisticRemoveFromShortlist: (propertyId: string, type: PropertyType) => {
				set(state => {
					if (type === PropertyType.UNIT) {
						// Handle unit shortlist removal
						const unitIndex = state.buyerUnits.findIndex(unit => unit.unit?.id === propertyId);

						if (unitIndex !== -1) {
							// Remove shortlisting from unit
							const unit = state.buyerUnits[unitIndex];
							if (unit.processDetails) {
								unit.processDetails = unit.processDetails.filter(pd => pd.process !== "shortlisting");
							}
						}

						// Recompute shortlisted units
						state.shortlistedUnits = state.buyerUnits.filter(unit =>
							unit.processDetails?.some(pd => pd.process === "shortlisting" && pd.stage === "done"),
						);
						state.shortlistedUnitIds = buildIdMap(state.shortlistedUnits, (unit: BuyerUnit) => unit.unit?.id);
					} else if (type === PropertyType.PROJECT) {
						// Handle project shortlist removal
						if (state.buyerProjects) {
							const projectIndex = state.buyerProjects.findIndex(project => project.project?.id === propertyId);

							if (projectIndex !== -1) {
								// Remove shortlisting from project
								const project = state.buyerProjects[projectIndex];
								if (project.processDetails) {
									project.processDetails = project.processDetails.filter(pd => pd.process !== "shortlisting");
								}
							}

							// Recompute shortlisted projects
							state.shortlistedProjects = state.buyerProjects.filter(project =>
								project.processDetails?.some(pd => pd.process === "shortlisting" && pd.stage === "done"),
							);
							state.shortlistedProjectIds = buildIdMap(
								state.shortlistedProjects,
								(project: BuyerProject) => project.project?.id,
							);
						}
					}
				});
			},

			// Manual optimistic updates - same logic but separate methods for different contexts
			optimisticAddToShortlistManual: (propertyId: string, type: PropertyType) => {
				set(state => {
					if (type === PropertyType.UNIT) {
						// Handle unit shortlisting
						let existingUnitIndex = state.buyerUnits.findIndex(unit => unit.unit?.id === propertyId);

						if (existingUnitIndex !== -1) {
							// Update existing unit
							const unit = state.buyerUnits[existingUnitIndex];
							if (!unit.processDetails) {
								unit.processDetails = [];
							}

							// Remove any existing shortlisting process
							unit.processDetails = unit.processDetails.filter(pd => pd.process !== "shortlisting");

							// Add new shortlisting process
							unit.processDetails.push({
								process: "shortlisting",
								status: "active",
								stage: "done",
								incomplete: false,
								appointmentDate: new Date(),
								visitExec: "",
								updatedAt: new Date(),
								updatedBy: "",
							});
						} else {
							// Create new unit entry
							const newUnit = {
								id: `temp-unit-manual-${propertyId}-${Date.now()}`,
								unit: { id: propertyId },
								processDetails: [
									{
										process: "shortlisting",
										status: "active",
										stage: "done",
										incomplete: false,
										appointmentDate: new Date(),
										visitExec: "",
										updatedAt: new Date(),
										updatedBy: "",
									},
								],
							};
							state.buyerUnits.push(newUnit);
						}

						// Recompute shortlisted units
						state.shortlistedUnits = state.buyerUnits.filter(unit =>
							unit.processDetails?.some(pd => pd.process === "shortlisting" && pd.stage === "done"),
						);
						state.shortlistedUnitIds = buildIdMap(state.shortlistedUnits, (unit: BuyerUnit) => unit.unit?.id);
					} else if (type === PropertyType.PROJECT) {
						// Handle project shortlisting
						if (!state.buyerProjects) {
							state.buyerProjects = [];
						}

						let existingProjectIndex = state.buyerProjects.findIndex(project => project.project?.id === propertyId);

						if (existingProjectIndex !== -1) {
							// Update existing project
							const project = state.buyerProjects[existingProjectIndex];
							if (!project.processDetails) {
								project.processDetails = [];
							}

							// Remove any existing shortlisting process
							project.processDetails = project.processDetails.filter(pd => pd.process !== "shortlisting");

							// Add new shortlisting process
							project.processDetails.push({
								process: "shortlisting",
								status: "active",
								stage: "done",
								incomplete: false,
								appointmentDate: new Date(),
								visitExec: "",
								updatedAt: new Date(),
								updatedBy: "",
							});
						} else {
							// Create new project entry
							const processDetail = {
								process: "shortlisting",
								status: "active",
								stage: "done",
								incomplete: false,
								appointmentDate: new Date(),
								visitExec: "",
								updatedAt: new Date(),
								updatedBy: "",
							};

							const newProject = {
								id: `temp-project-manual-${propertyId}-${Date.now()}`,
								project: { id: propertyId, name: "" },
								sellerProject: { id: propertyId },
								group: { id: state.buyerGroupId || "" },
								processDetails: [processDetail],
								currentProcessDetails: processDetail,
								cancellation: { flag: false },
							};
							state.buyerProjects.push(newProject);
						}

						// Recompute shortlisted projects
						state.shortlistedProjects = state.buyerProjects.filter(project =>
							project.processDetails?.some(pd => pd.process === "shortlisting" && pd.stage === "done"),
						);
						state.shortlistedProjectIds = buildIdMap(
							state.shortlistedProjects,
							(project: BuyerProject) => project.project?.id,
						);
					}
				});
			},

			optimisticRemoveFromShortlistManual: (propertyId: string, type: PropertyType) => {
				set(state => {
					if (type === PropertyType.UNIT) {
						// Handle unit shortlist removal
						const unitIndex = state.buyerUnits.findIndex(unit => unit.unit?.id === propertyId);

						if (unitIndex !== -1) {
							// Remove shortlisting from unit
							const unit = state.buyerUnits[unitIndex];
							if (unit.processDetails) {
								unit.processDetails = unit.processDetails.filter(pd => pd.process !== "shortlisting");
							}
						}

						// Recompute shortlisted units
						state.shortlistedUnits = state.buyerUnits.filter(unit =>
							unit.processDetails?.some(pd => pd.process === "shortlisting" && pd.stage === "done"),
						);
						state.shortlistedUnitIds = buildIdMap(state.shortlistedUnits, (unit: BuyerUnit) => unit.unit?.id);
					} else if (type === PropertyType.PROJECT) {
						// Handle project shortlist removal
						if (state.buyerProjects) {
							const projectIndex = state.buyerProjects.findIndex(project => project.project?.id === propertyId);

							if (projectIndex !== -1) {
								// Remove shortlisting from project
								const project = state.buyerProjects[projectIndex];
								if (project.processDetails) {
									project.processDetails = project.processDetails.filter(pd => pd.process !== "shortlisting");
								}
							}

							// Recompute shortlisted projects
							state.shortlistedProjects = state.buyerProjects.filter(project =>
								project.processDetails?.some(pd => pd.process === "shortlisting" && pd.stage === "done"),
							);
							state.shortlistedProjectIds = buildIdMap(
								state.shortlistedProjects,
								(project: BuyerProject) => project.project?.id,
							);
						}
					}
				});
			},

			// Visit request optimistic updates
			// optimisticAddToVisitRequest: (propertyId: string) => {
			// 	set(state => {
			// 		// First, check if this property exists in units
			// 		let existingUnitIndex = state.buyerUnits.findIndex(unit => unit.unit?.id === propertyId);

			// 		// Also check if this property exists in projects
			// 		let existingProjectIndex = -1;
			// 		if (state.buyerProjects) {
			// 			existingProjectIndex = state.buyerProjects.findIndex(project => project.project?.id === propertyId);
			// 		}

			// 		if (existingUnitIndex !== -1) {
			// 			// Update existing unit
			// 			const unit = state.buyerUnits[existingUnitIndex];
			// 			if (!unit.processDetails) {
			// 				unit.processDetails = [];
			// 			}

			// 			// Remove any existing visiting process
			// 			unit.processDetails = unit.processDetails.filter(pd => pd.process !== "visiting");

			// 			// Add new visiting process
			// 			unit.processDetails.push({
			// 				process: "visiting",
			// 				status: "active",
			// 				stage: "requested",
			// 				incomplete: false,
			// 				appointmentDate: new Date(),
			// 				visitExec: "",
			// 				updatedAt: new Date(),
			// 				updatedBy: "",
			// 			});
			// 		} else if (existingProjectIndex !== -1 && state.buyerProjects) {
			// 			// Update existing project
			// 			const project = state.buyerProjects[existingProjectIndex];
			// 			if (!project.processDetails) {
			// 				project.processDetails = [];
			// 			}

			// 			// Remove any existing visiting process
			// 			project.processDetails = project.processDetails.filter(pd => pd.process !== "visiting");

			// 			// Add new visiting process
			// 			project.processDetails.push({
			// 				process: "visiting",
			// 				status: "active",
			// 				stage: "requested",
			// 				incomplete: false,
			// 				appointmentDate: new Date(),
			// 				visitExec: "",
			// 				updatedAt: new Date(),
			// 				updatedBy: "",
			// 			});
			// 		} else {
			// 			// Property not found in either collection, create new unit entry as fallback
			// 			const newUnit = {
			// 				id: `temp-visit-${propertyId}-${Date.now()}`,
			// 				unit: { id: propertyId },
			// 				processDetails: [
			// 					{
			// 						process: "visiting",
			// 						status: "active",
			// 						stage: "requested",
			// 						incomplete: false,
			// 						appointmentDate: new Date(),
			// 						visitExec: "",
			// 						updatedAt: new Date(),
			// 						updatedBy: "",
			// 					},
			// 				],
			// 			};
			// 			state.buyerUnits.push(newUnit);
			// 		}

			// 		// Recompute visit requested units and projects
			// 		state.visitRequestedUnits = state.buyerUnits.filter(unit =>
			// 			unit.processDetails?.some(pd => pd.process === "visiting" && pd.stage === "requested"),
			// 		);

			// 		if (state.buyerProjects) {
			// 			state.visitRequestedProjects = state.buyerProjects.filter(project =>
			// 				project.processDetails?.some(pd => pd.process === "visiting" && pd.stage === "requested"),
			// 			);
			// 		}
			// 	});
			// },

			// optimisticRemoveFromVisitRequest: (propertyId: string) => {
			// 	set(state => {
			// 		// Check if this property exists in units
			// 		const unitIndex = state.buyerUnits.findIndex(unit => unit.unit?.id === propertyId);

			// 		// Also check if this property exists in projects
			// 		let projectIndex = -1;
			// 		if (state.buyerProjects) {
			// 			projectIndex = state.buyerProjects.findIndex(project => project.project?.id === propertyId);
			// 		}

			// 		if (unitIndex !== -1) {
			// 			// Remove visiting from unit
			// 			const unit = state.buyerUnits[unitIndex];
			// 			if (unit.processDetails) {
			// 				unit.processDetails = unit.processDetails.filter(pd => pd.process !== "visiting");
			// 			}
			// 		}

			// 		if (projectIndex !== -1 && state.buyerProjects) {
			// 			// Remove visiting from project
			// 			const project = state.buyerProjects[projectIndex];
			// 			if (project.processDetails) {
			// 				project.processDetails = project.processDetails.filter(pd => pd.process !== "visiting");
			// 			}
			// 		}

			// 		// Recompute visit requested units and projects
			// 		state.visitRequestedUnits = state.buyerUnits.filter(unit =>
			// 			unit.processDetails?.some(pd => pd.process === "visiting" && pd.stage === "requested"),
			// 		);

			// 		if (state.buyerProjects) {
			// 			state.visitRequestedProjects = state.buyerProjects.filter(project =>
			// 				project.processDetails?.some(pd => pd.process === "visiting" && pd.stage === "requested"),
			// 			);
			// 		}
			// 	});
			// },

			// optimisticAddToVisitRequestManual: (propertyId: string) => {
			// 	set(state => {
			// 		// First, check if this property exists in units
			// 		let existingUnitIndex = state.buyerUnits.findIndex(unit => unit.unit?.id === propertyId);

			// 		// Also check if this property exists in projects
			// 		let existingProjectIndex = -1;
			// 		if (state.buyerProjects) {
			// 			existingProjectIndex = state.buyerProjects.findIndex(project => project.project?.id === propertyId);
			// 		}

			// 		if (existingUnitIndex !== -1) {
			// 			// Update existing unit
			// 			const unit = state.buyerUnits[existingUnitIndex];
			// 			if (!unit.processDetails) {
			// 				unit.processDetails = [];
			// 			}

			// 			// Remove any existing visiting process
			// 			unit.processDetails = unit.processDetails.filter(pd => pd.process !== "visiting");

			// 			// Add new visiting process
			// 			unit.processDetails.push({
			// 				process: "visiting",
			// 				status: "active",
			// 				stage: "requested",
			// 				incomplete: false,
			// 				appointmentDate: new Date(),
			// 				visitExec: "",
			// 				updatedAt: new Date(),
			// 				updatedBy: "",
			// 			});
			// 		} else if (existingProjectIndex !== -1 && state.buyerProjects) {
			// 			// Update existing project
			// 			const project = state.buyerProjects[existingProjectIndex];
			// 			if (!project.processDetails) {
			// 				project.processDetails = [];
			// 			}

			// 			// Remove any existing visiting process
			// 			project.processDetails = project.processDetails.filter(pd => pd.process !== "visiting");

			// 			// Add new visiting process
			// 			project.processDetails.push({
			// 				process: "visiting",
			// 				status: "active",
			// 				stage: "requested",
			// 				incomplete: false,
			// 				appointmentDate: new Date(),
			// 				visitExec: "",
			// 				updatedAt: new Date(),
			// 				updatedBy: "",
			// 			});
			// 		} else {
			// 			// Property not found in either collection, create new unit entry as fallback
			// 			const newUnit = {
			// 				id: `temp-visit-manual-${propertyId}-${Date.now()}`,
			// 				unit: { id: propertyId },
			// 				processDetails: [
			// 					{
			// 						process: "visiting",
			// 						status: "active",
			// 						stage: "requested",
			// 						incomplete: false,
			// 						appointmentDate: new Date(),
			// 						visitExec: "",
			// 						updatedAt: new Date(),
			// 						updatedBy: "",
			// 					},
			// 				],
			// 			};
			// 			state.buyerUnits.push(newUnit);
			// 		}

			// 		// Recompute visit requested units and projects
			// 		state.visitRequestedUnits = state.buyerUnits.filter(unit =>
			// 			unit.processDetails?.some(pd => pd.process === "visiting" && pd.stage === "requested"),
			// 		);

			// 		if (state.buyerProjects) {
			// 			state.visitRequestedProjects = state.buyerProjects.filter(project =>
			// 				project.processDetails?.some(pd => pd.process === "visiting" && pd.stage === "requested"),
			// 			);
			// 		}
			// 	});
			// },

			// optimisticRemoveFromVisitRequestManual: (propertyId: string) => {
			// 	set(state => {
			// 		// Check if this property exists in units
			// 		const unitIndex = state.buyerUnits.findIndex(unit => unit.unit?.id === propertyId);

			// 		// Also check if this property exists in projects
			// 		let projectIndex = -1;
			// 		if (state.buyerProjects) {
			// 			projectIndex = state.buyerProjects.findIndex(project => project.project?.id === propertyId);
			// 		}

			// 		if (unitIndex !== -1) {
			// 			// Remove visiting from unit
			// 			const unit = state.buyerUnits[unitIndex];
			// 			if (unit.processDetails) {
			// 				unit.processDetails = unit.processDetails.filter(pd => pd.process !== "visiting");
			// 			}
			// 		}

			// 		if (projectIndex !== -1 && state.buyerProjects) {
			// 			// Remove visiting from project
			// 			const project = state.buyerProjects[projectIndex];
			// 			if (project.processDetails) {
			// 				project.processDetails = project.processDetails.filter(pd => pd.process !== "visiting");
			// 			}
			// 		}

			// 		// Recompute visit requested units and projects
			// 		state.visitRequestedUnits = state.buyerUnits.filter(unit =>
			// 			unit.processDetails?.some(pd => pd.process === "visiting" && pd.stage === "requested"),
			// 		);

			// 		if (state.buyerProjects) {
			// 			state.visitRequestedProjects = state.buyerProjects.filter(project =>
			// 				project.processDetails?.some(pd => pd.process === "visiting" && pd.stage === "requested"),
			// 			);
			// 		}
			// 	});
			// },
		})),
		{ name: "buyer-properties-store" },
	),
);
